import { useEffect } from 'react';
import { readRegionCookie } from './regionCookie';
import { RegionCode, regionByCode, regionForHost, regionSwitchUrl } from './regions';

/**
 * Sends a returning visitor to the region they last chose (CNA-1902 / CNA-1906).
 *
 * This is the half of the cookie that makes subdomains worth having: the decision log
 * chose them over separate domains precisely so a choice could be remembered and acted on
 * before authentication. Writing the cookie without ever reading it would leave that
 * promise unimplemented.
 *
 * Deliberately narrow, because an automatic redirect is easy to get wrong:
 *
 * - It only acts on a host that is itself a known region. On `localhost`, a PR preview,
 *   `stage.cinareo.com` or `prestage.cinareo.com`, `regionForHost` returns undefined and
 *   this does nothing — those are environments, not regions, and must never be redirected
 *   out of. (The cookie is also scoped so they cannot see it; this is the second guard.)
 * - It only acts when the remembered region is a *different* known region, so it cannot
 *   loop: after the redirect the host matches the cookie and the condition is false.
 * - An unrecognised cookie value is ignored rather than resolved to the default, which
 *   would bounce a visitor off a perfectly good host.
 *
 * Consequence worth knowing, and the same as Datadog's: once a region is remembered,
 * typing the other region's URL directly will redirect away from it. Changing region is
 * done through the switcher, which writes the cookie before navigating.
 */
export const useRegionRedirect = (): void => {
  useEffect(() => {
    const host = globalThis.location?.host ?? '';
    const currentRegion = regionForHost(host);
    if (!currentRegion) return;

    const remembered = readRegionCookie();
    if (!remembered || remembered === currentRegion.code) return;

    const target = regionByCode(remembered as RegionCode);
    // Undefined when the cookie names a region this environment has no domain for — a stale
    // value, or one switched off in config. Treat it as "no usable preference" rather than
    // redirecting somewhere the visitor never chose.
    if (!target || target.code === currentRegion.code) return;

    globalThis.location.replace(regionSwitchUrl(target, globalThis.location));
  }, []);
};
